import React, { useState, useEffect, useCallback } from "react";

import UploadStep from "./UploadStep";
import ValidationStep from "./ValidationStep";
import EditStep from "./EditStep";
import SubmitStep from "./SubmitStep";
import FailedRecordsStep from "./FailedRecordsStep";
import useUploadsQueries from "../../../store/uploads/uploadsQueries";
import "./EditStep.css";

const UploadPopUp = ({
  handleCloseModal,
  schoolId,
  selectedEvent,
  uploadType, // "eventCreate" | "eventReschedule" | "eventDelete" | "studentRegister" | "uploadOfflineResponses"
  selectedTab,
}) => {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validData, setValidData] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [jobId, setJobId] = useState();
  const [uploadStatusData, setUploadStatusData] = useState({});
  const [refetchStatus, setRefetchStatus] = useState(null);

  const {
    handleBulkRegister,
    handleBulkUpdate,
    handleGetBulkRecordStatus,
    handleBulkEventCreate,
    handleBulkEventReschedule,
    handleBulkEventDelete,
    handleBulkUploadOfflineResponses,
  } = useUploadsQueries();

  const getUploadTypeTitle = () => {
    switch (uploadType) {
      case "eventCreate":
        return "Bulk Event Creation";
      case "eventReschedule":
        return "Bulk Event Rescheduling";
      case "eventDelete":
        return "Bulk Event Deletion";
      case "studentRegister":
        return "Bulk Student Registration";
      case "uploadOfflineResponses":
        return "Bulk Upload Offline Responses";
      default:
        return "Import File";
    }
  };

  useEffect(() => {
    if (
      selectedTab !== "Onboarded Students" ||
      uploadType !== "studentRegister"
    ) {
      setStep(1);
    }
  }, [selectedTab, uploadType]);

  useEffect(() => {
    if (jobId) {
      const { statusData, refetchStatus: fetchStatus } =
        handleGetBulkRecordStatus(jobId, uploadType);
      setRefetchStatus(() => fetchStatus);
      if (statusData) {
        setUploadStatusData(statusData);
      }
    }
  }, [jobId]);

  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  const handleFileChange = (data) => {
    setCsvData(data);
    nextStep();
  };

  const handleValidationResult = useCallback((valid, errors) => {
    setValidData(valid);
    setValidationErrors(errors);
    setIsValid(errors.length === 0);
    setIsValidating(false);
  }, []);

  const handleUpdateCsvData = (data) => {
    setCsvData(data);
    nextStep();
  };

  const handleSubmit = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append("fileobject", file);

    if (
      uploadType === "studentRegister" ||
      uploadType === "uploadOfflineResponses"
    ) {
      formData.append(
        "event_scheduled",
        selectedEvent ? selectedEvent.scheduled_events.join(",") : ""
      );
    }

    try {
      let response;
      switch (uploadType) {
        case "eventCreate":
          response = await handleBulkEventCreate(schoolId, formData);
          break;
        case "eventReschedule":
          response = await handleBulkEventReschedule(schoolId, formData);
          break;
        case "eventDelete":
          response = await handleBulkEventDelete(schoolId, formData);
          break;
        case "studentRegister":
          response = await handleBulkRegister(schoolId, formData);
          break;
        case "uploadOfflineResponses":
          response = await handleBulkUploadOfflineResponses(schoolId, formData);
          break;
        default:
          response = await handleBulkUpdate(schoolId, formData);
      }

      setUploadMsg(response.id);
      setJobId(response.jobId);
    } catch (error) {
      console.error("Error:", error);
      setUploadMsg(`Error: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const upoloadeStatus = async () => {
    setLoadingStatus(true);
    try {
      if (refetchStatus) {
        const data = await refetchStatus();
        if (data) {
          setUploadStatusData(data);
        } else {
          throw new Error();
        }
      }
    } catch (error) {
      setUploadStatusData({
        status: "Bulk upload job not found",
      });
      console.error("Error fetching status:", error);
    } finally {
      setLoadingStatus(false);
    }
  };

  return (
    <div className="overlay">
      <div className="overlay-content">
        <h1>{getUploadTypeTitle()}</h1>
        <div className="line"></div>
        <div className="uploadModal-content">
          <div>
            {step === 0 && selectedTab === "Onboarded Students" && (
              <div>
                <p className="declaration">
                  <span>Attention:</span> This process registers students with
                  the school.
                  <br /> You will need to assign them to events separately from
                  events tab.
                </p>
              </div>
            )}
            {step === 1 && (
              <UploadStep
                setFile={setFile}
                uploadType={uploadType}
                handleFileChange={handleFileChange}
              />
            )}
            {step === 2 && (
              <ValidationStep
                csvData={csvData}
                handleValidationResult={handleValidationResult}
                schoolId={schoolId}
                uploadType={uploadType}
              />
            )}
            {step === 3 && (
              <EditStep
                csvData={csvData}
                validationErrors={validationErrors}
                handleUpdateCsvData={handleUpdateCsvData}
                uploadType={uploadType}
              />
            )}
            {step === 4 && (
              <SubmitStep
                loading={loading}
                uploadMsg={uploadMsg}
                validData={validData}
                handleSubmit={handleSubmit}
                upoloadeStatus={upoloadeStatus}
                uploadStatusData={uploadStatusData}
                jobId={jobId}
                loadingStatus={loadingStatus}
              />
            )}
            {step === 5 && (
              <FailedRecordsStep uploadStatusData={uploadStatusData} />
            )}
          </div>
        </div>
        <div className="popupobutnCont">
          {step < 1 && (
            <button onClick={() => setStep(1)} className="saveBtn">
              OK
            </button>
          )}
          {step < 2 && (
            <button
              onClick={handleCloseModal}
              className="closeBtn"
              disabled={step === 2 && isValidating}
            >
              Close
            </button>
          )}
          {step > 2 && (
            <button
              onClick={handleCloseModal}
              className="closeBtn"
              disabled={step === 2 && isValidating}
            >
              Close
            </button>
          )}
          {step > 1 && step !== 4 && (
            <button
              onClick={prevStep}
              className="closeBtn"
              disabled={step === 2 && isValidating}
            >
              Back
            </button>
          )}
          {step === 2 && !isValidating && !isValid && (
            <button onClick={() => setStep(3)} className="saveBtn">
              View
            </button>
          )}
          {step === 2 && !isValidating && isValid && (
            <button onClick={() => setStep(4)} className="saveBtn">
              Next
            </button>
          )}
          {step === 4 && uploadMsg === "" && (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="saveBtn"
            >
              Submit
            </button>
          )}
          {step === 4 && uploadStatusData?.status === "failed" && (
            <button onClick={() => setStep(5)} className="saveBtn">
              View Failed Records
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadPopUp;
